import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { findDrink, price } from "@/content/menu";
import { OrderButton } from "@/components/site/OrderContext";
import { MaskLines, Reveal, EASE } from "@/components/site/motion";

const ZONES = [
  { key: "strawberry", word: "Strawberry", bg: "#ff4f7b", fg: "#fff5e8", drink: "strawberry-milk-tea", fx: "/img/fx-strawberry.webp", cat: "milk-tea" },
  { key: "matcha", word: "Matcha", bg: "#2e9e4f", fg: "#fff5e8", drink: "japanese-matcha-milk-tea", fx: "/img/fx-leaf.webp", cat: "milk-tea" },
  { key: "mango", word: "Mango", bg: "#ff8a00", fg: "#1a0a10", drink: "mango-milk-tea", fx: "/img/fx-mango.webp", cat: "milk-tea" },
  { key: "coffee", word: "Cà Phê", bg: "#2a160c", fg: "#fff5e8", drink: "seasalt-coffee-og", image: "/img/mood-coffee-drip.webp", cat: "vietnamese-coffee" },
];

const Zone = ({ z, i, onEnter }) => {
  const ref = useRef(null);
  const drink = findDrink(z.drink);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  const wordX = useTransform(scrollYProgress, [0, 1], ["12%", "-22%"]);
  const fxY = useTransform(scrollYProgress, [0, 1], [120, -160]);
  const img = z.image || drink.image;

  return (
    <motion.article ref={ref} data-testid={`flavor-zone-${z.key}`} onViewportEnter={onEnter} viewport={{ margin: "-50% 0px -50% 0px" }} className="relative flex min-h-[100svh] items-center overflow-hidden py-24">
      <motion.p aria-hidden style={{ x: wordX }} className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 select-none whitespace-nowrap font-display text-[34vw] font-extrabold uppercase leading-none tracking-tighter opacity-[0.12] lg:text-[24vw]">
        {z.word}
      </motion.p>
      <div className="wrap relative grid items-center gap-12 lg:grid-cols-12">
        <div className={`relative lg:col-span-5 ${i % 2 ? "lg:order-2 lg:col-start-8" : ""}`}>
          <Reveal y={60} className="relative mx-auto aspect-[4/5] w-full max-w-[460px] overflow-hidden rounded-t-[999px] rounded-b-[2.5rem] shadow-[0_40px_80px_-30px_rgba(0,0,0,0.55)]">
            <motion.img src={img} alt={z.image ? "Vietnamese coffee dripping through a phin filter" : `${drink.name} from TeaDM`} style={{ y: imgY }} className="absolute inset-0 h-[120%] w-full -top-[10%] object-cover" loading="lazy" />
          </Reveal>
          {z.fx && (
            <motion.img src={z.fx} alt="" aria-hidden style={{ y: fxY }} className="pointer-events-none absolute -right-2 top-[8%] w-24 drop-shadow-2xl sm:w-32 lg:-right-10" />
          )}
        </div>
        <div className={`lg:col-span-6 ${i % 2 ? "lg:order-1 lg:col-start-1" : "lg:col-start-7"}`}>
          <p className="eyebrow mb-6 opacity-80">Flavor 0{i + 1} / {z.word}</p>
          <MaskLines as="h3" lines={drink.name.split(" ").length > 3 ? [drink.name.split(" ").slice(0, 2).join(" "), drink.name.split(" ").slice(2).join(" ")] : [drink.name]} className="font-display text-5xl font-extrabold uppercase leading-[0.9] tracking-tight sm:text-7xl" />
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-lg text-lg opacity-90">{drink.desc || drink.specs.join(" · ")}</p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <span className="font-display text-3xl font-extrabold">{price(drink.price)}</span>
              <OrderButton className="btn btn-cream" testId={`zone-order-${z.key}`}>Order this</OrderButton>
              <Link to={`/menu#${z.cat}`} className="btn btn-ghost" data-testid={`zone-menu-${z.key}`}>See in menu <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </Reveal>
        </div>
      </div>
    </motion.article>
  );
};

export const FlavorZones = () => {
  const [active, setActive] = useState(0);
  const z = ZONES[active];
  return (
    <motion.section data-testid="flavor-zones" aria-label="Flavor worlds" className="grain relative" style={{ backgroundColor: ZONES[0].bg, color: ZONES[0].fg }} animate={{ backgroundColor: z.bg, color: z.fg }} transition={{ duration: 0.8, ease: EASE }}>
      <div className="wrap pt-24 sm:pt-32">
        <p className="eyebrow opacity-80">Scroll the flavors</p>
        <MaskLines lines={["Every flavor gets", "its own world."]} className="mt-4 font-display text-5xl font-extrabold uppercase leading-[0.9] tracking-tight sm:text-7xl lg:text-8xl" />
      </div>
      {ZONES.map((zone, i) => <Zone key={zone.key} z={zone} i={i} onEnter={() => setActive(i)} />)}
    </motion.section>
  );
};
